// src/components/AssignTrainingSection.jsx
import { useState } from "react";
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  serverTimestamp, // ✅ NEW
} from "firebase/firestore";
import Select from "react-select";
import { toast } from "react-toastify";
import { db } from "../firebase/config";

export default function AssignTrainingSection({
  companyId,
  employees,
  trainingCourses,
  setTrainingCourses,
  selectedEmployee,
  setSelectedEmployee,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [confirmAssign, setConfirmAssign] = useState(false);
  const [assignmentDeadline, setAssignmentDeadline] = useState("");

  const filteredCourses = trainingCourses.filter((course) =>
    course.courseTitle.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isAssigned = (course) =>
    course.assignedEmployees?.includes(selectedEmployee);

  const toggleSelectCourse = (id) => {
    setSelectedCourses((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleDeleteCourse = async (courseId) => {
    try {
      const courseRef = doc(
        db,
        `companies/${companyId}/training_courses`,
        courseId,
      );
      await deleteDoc(courseRef);

      const course = trainingCourses.find((c) => c.id === courseId);
      const assigned = course?.assignedEmployees || [];

      await Promise.all(
        assigned.map(async (empId) => {
          const progressRef = doc(
            db,
            `companies/${companyId}/employees/${empId}/training_progress`,
            courseId,
          );
          await deleteDoc(progressRef);
        }),
      );

      setTrainingCourses((prev) => prev.filter((c) => c.id !== courseId));
      toast.success("Course deleted successfully.");
    } catch (err) {
      console.error("Delete failed", err.message);
      toast.error("Failed to delete course.");
    }
  };

  const notifyEmployeeTrainingAssigned = async ({
    employeeId,
    courseId,
    courseTitle,
    deadline,
  }) => {
    if (!companyId || !employeeId || !courseId) return;

    // normalize deadline into a stable string to avoid duplicates
    const deadlineKey = deadline?.toDate?.()
      ? deadline
          .toDate()
          .toISOString()
          .slice(0, 10)
      : deadline
      ? new Date(deadline).toISOString().slice(0, 10)
      : "no-deadline";

    // ✅ deterministic ID so you don't spam duplicates
    const notifId = `trainingAssigned_${employeeId}_${courseId}_${deadlineKey}`;

    await setDoc(
      doc(db, `users/${employeeId}/notifications`, notifId),
      {
        type: "trainingAssigned",
        title: "New training assigned",
        message:
          deadlineKey === "no-deadline"
            ? `📘 "${courseTitle}" was assigned to you.`
            : `📘 "${courseTitle}" was assigned to you. Due: ${deadlineKey}.`,

        companyId,
        employeeId,
        courseId,
        courseTitle,
        deadline: deadlineKey === "no-deadline" ? null : deadlineKey,

        read: false,
        archived: false,
        createdAt: serverTimestamp(),
      },
      { merge: true },
    );
  };

  const deadlineToStore = assignmentDeadline
    ? new Date(`${assignmentDeadline}T00:00:00`).toISOString()
    : null;

  const handleAssignAll = async () => {
    try {
      for (const courseId of selectedCourses) {
        const course = trainingCourses.find((c) => c.id === courseId);

        // ✅ write an assignedAt timestamp in the employee's training_progress doc
        await setDoc(
          doc(
            db,
            `companies/${companyId}/employees/${selectedEmployee}/training_progress`,
            courseId,
          ),
          {
            courseId,
            status: "pending",
            deadline: deadlineToStore,
            assignedAt: serverTimestamp(),
          },
          { merge: true },
        );

        await notifyEmployeeTrainingAssigned({
          employeeId: selectedEmployee,
          courseId,
          courseTitle: course?.courseTitle || "Training",
          deadline: deadlineToStore,
        });

        // Update the course's assignedEmployees array
        const courseRef = doc(
          db,
          `companies/${companyId}/training_courses`,
          courseId,
        );
        const courseDoc = await getDoc(courseRef);
        const updated = Array.from(
          new Set([
            ...(courseDoc.data().assignedEmployees || []),
            selectedEmployee,
          ]),
        );
        await updateDoc(courseRef, { assignedEmployees: updated });

        // Update local state
        setTrainingCourses((prev) =>
          prev.map((c) =>
            c.id === courseId ? { ...c, assignedEmployees: updated } : c,
          ),
        );
      }

      toast.success("Assigned successfully!");

      setSelectedCourses([]);
      setConfirmAssign(false);
      setAssignmentDeadline("");

      // quick setup flag
      await updateDoc(doc(db, "companies", companyId), {
        "quickSetupProgress.assignedTraining": true,
      });
    } catch (err) {
      console.error("Assignment failed", err);
      toast.error("Assignment failed.");
    }
  };

  const handleWithdraw = async (courseId) => {
    try {
      // Remove training_progress for this employee + course
      await deleteDoc(
        doc(
          db,
          `companies/${companyId}/employees/${selectedEmployee}/training_progress`,
          courseId,
        ),
      );

      // Remove employee from assignedEmployees on the course doc
      const courseRef = doc(
        db,
        `companies/${companyId}/training_courses`,
        courseId,
      );
      const courseDoc = await getDoc(courseRef);
      const updated = (courseDoc.data().assignedEmployees || []).filter(
        (id) => id !== selectedEmployee,
      );
      await updateDoc(courseRef, { assignedEmployees: updated });

      // Update local state
      setTrainingCourses((prev) =>
        prev.map((c) =>
          c.id === courseId ? { ...c, assignedEmployees: updated } : c,
        ),
      );
      toast.success("Withdrawn successfully!");
    } catch (err) {
      console.error("Withdraw failed", err);
      toast.error("Withdraw failed.");
    }
  };

  const fmtDate = (value) => {
    if (!value) return "—";

    // Handles Firestore Timestamp, JS Date, ISO string, or millis
    const d = value?.toDate?.()
      ? value.toDate()
      : value instanceof Date
      ? value
      : new Date(value);

    if (Number.isNaN(d.getTime())) return "—";

    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  };

  return (
    <div className="training-card">
      <h2>Assign Training</h2>

      <label>Select Employee:</label>
      <Select
        options={employees.map((e) => ({ value: e.id, label: e.name }))}
        value={
          selectedEmployee
            ? {
                value: selectedEmployee,
                label: employees.find((e) => e.id === selectedEmployee)?.name,
              }
            : null
        }
        onChange={(opt) => setSelectedEmployee(opt?.value || "")}
        isClearable
      />

      <input
        type="text"
        placeholder="Search courses..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{ margin: "1rem 0", padding: "8px", width: "100%" }}
      />
      <label style={{ marginTop: "10px", display: "block" }}>
        Assignment Deadline
      </label>
      <input
        type="date"
        value={assignmentDeadline}
        onChange={(e) => setAssignmentDeadline(e.target.value)}
        style={{ margin: "0.5rem 0 1rem", padding: "8px", width: "100%" }}
      />

      {selectedCourses.length > 0 && (
        <button
          className="training-button"
          onClick={() => setConfirmAssign(true)}
        >
          ✅ Assign {selectedCourses.length} Selected Courses
        </button>
      )}

      <div className="training-course-list">
        {filteredCourses.map((course) => (
          <div
            key={course.id}
            className="training-course-card"
            style={{
              background:
                "linear-gradient(135deg, #164766 0%, #1f5a6f 35%, #9B643A 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
            }}
          >
            <h3>{course.courseTitle}</h3>
            <p>{course.description}</p>
            <p>📅 Deadline: Set when assigned</p>

            <p>
              🏷 Type:{" "}
              {course.type === "video"
                ? "Video"
                : course.type === "pdf"
                ? "PDF"
                : "External Link"}
            </p>

            {course.type === "video" && (
              <a href={course.contentUrl} target="_blank" rel="noreferrer">
                🎥 Watch Video
              </a>
            )}
            {course.type === "pdf" && (
              <a href={course.contentUrl} target="_blank" rel="noreferrer">
                📄 View PDF
              </a>
            )}
            {course.type === "link" && (
              <a href={course.contentUrl} target="_blank" rel="noreferrer">
                🔗 Visit Course
              </a>
            )}

            <div className="training-card-actions">
              {isAssigned(course) ? (
                <button onClick={() => handleWithdraw(course.id)}>
                  Withdraw
                </button>
              ) : (
                <label className="checkbox-label course-checkbox-bottom">
                  Select to assign
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course.id)}
                    onChange={() => toggleSelectCourse(course.id)}
                  />
                </label>
              )}

              <button
                onClick={() => {
                  const confirmDelete = window.confirm(
                    `Are you sure you want to delete "${course.courseTitle}"?`,
                  );
                  if (confirmDelete) handleDeleteCourse(course.id);
                }}
                style={{
                  marginLeft: "10px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "6px 10px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirmAssign && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Confirm Assignment</h3>
            <p>
              Assign {selectedCourses.length} course(s) to{" "}
              {employees.find((e) => e.id === selectedEmployee)?.name}?
            </p>
            <div className="modal-actions">
              <button className="general-savebuttons" onClick={handleAssignAll}>
                Confirm
              </button>
              <button
                className="general-cancelbuttons"
                onClick={() => setConfirmAssign(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
